import React, { useState, useEffect, useCallback } from 'react';
import { RefreshCw } from 'lucide-react';
import api from '../api';
import DraggableModal from '../components/DraggableModal';
import StatusBadge from '../components/StatusBadge';
import ActionButton from '../components/ActionButton';
import ErrorAlert from '../components/ErrorAlert';

function Row({ label, value }) {
  if (!value && value !== 0) return null;
  return (
    <div className="flex gap-2 py-1 border-b border-gray-50 last:border-0">
      <span className="text-xs text-gray-500 w-36 flex-shrink-0">{label}</span>
      <span className="text-xs text-gray-800">{String(value)}</span>
    </div>
  );
}

const REVIEW_STATUSES = ['pending', 'contacted', 'interested', 'not_interested', 'partner', 'rejected'];
const CONTACTED_STATUSES = ['not_contacted', 'email_sent', 'called', 'met', 'follow_up_needed'];
const PRIORITIES = ['low', 'medium', 'high', 'urgent'];

function Chips({ label, value, options, onChange }) {
  return (
    <div>
      <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">{label}</label>
      <div className="flex flex-wrap gap-2">
        {options.map(s => (
          <button
            key={s}
            type="button"
            onClick={() => onChange(s)}
            className={`px-3 py-1.5 text-xs rounded-lg font-medium border transition-all ${
              value === s ? 'bg-slate-800 text-white border-slate-800' : 'bg-white text-gray-600 border-gray-300 hover:border-slate-600'
            }`}
          >
            {s.replace(/_/g, ' ')}
          </button>
        ))}
      </div>
    </div>
  );
}

function BusinessModal({ biz, onClose, onSave }) {
  const [form, setForm] = useState({
    admin_priority: biz.admin_priority || '',
    admin_review_status: biz.admin_review_status || '',
    admin_contacted_status: biz.admin_contacted_status || '',
    admin_vault_notes: biz.admin_vault_notes || '',
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const save = async () => {
    setSaving(true);
    setError('');
    try {
      await onSave(biz.id, form);
      onClose();
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <DraggableModal isOpen onClose={onClose} title={biz.business_name} initialWidth={860} initialHeight={580}>
      <div className="flex h-full overflow-hidden">
        {/* Left info */}
        <div className="w-72 flex-shrink-0 bg-gray-50 border-r border-gray-200 overflow-y-auto p-4 space-y-4">
          <div>
            <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">Business</p>
            <Row label="Name" value={biz.business_name} />
            <Row label="Category" value={biz.category} />
            <Row label="Area" value={biz.area} />
            <Row label="Website" value={biz.website} />
            <Row label="Phone" value={biz.phone} />
          </div>
          <div>
            <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">Contact</p>
            <Row label="Name" value={biz.contact_name} />
            <Row label="Email" value={biz.contact_email} />
          </div>
          {biz.relevance_free_text && (
            <div>
              <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-1">Why they applied</p>
              <p className="text-xs text-gray-700 whitespace-pre-wrap">{biz.relevance_free_text}</p>
            </div>
          )}
          <Row label="Relevance points" value={biz.relevance_points} />
        </div>

        {/* Right edit */}
        <div className="flex-1 overflow-y-auto p-5 space-y-5">
          {error && <ErrorAlert error={error} onClose={() => setError('')} />}

          <Chips label="Priority" value={form.admin_priority} options={PRIORITIES} onChange={v => setForm(f => ({ ...f, admin_priority: v }))} />
          <Chips label="Review status" value={form.admin_review_status} options={REVIEW_STATUSES} onChange={v => setForm(f => ({ ...f, admin_review_status: v }))} />
          <Chips label="Contacted" value={form.admin_contacted_status} options={CONTACTED_STATUSES} onChange={v => setForm(f => ({ ...f, admin_contacted_status: v }))} />

          <div>
            <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">Vault notes</label>
            <textarea
              rows={5}
              value={form.admin_vault_notes}
              onChange={e => setForm(f => ({ ...f, admin_vault_notes: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="Internal notes…"
            />
          </div>

          <div className="flex gap-3 pt-2 border-t border-gray-100">
            <ActionButton variant="primary" onClick={save} loading={saving}>Save</ActionButton>
            <ActionButton variant="secondary" onClick={onClose}>Close</ActionButton>
          </div>
        </div>
      </div>
    </DraggableModal>
  );
}

export default function BusinessesView() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedId, setSelectedId] = useState(null);
  const [search, setSearch] = useState('');

  const load = useCallback(async () => {
    try {
      const data = await api.getBusinesses();
      setItems(data);
      setError('');
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleSave = async (id, body) => {
    const updated = await api.patchBusiness(id, body);
    setItems(is => is.map(i => (i.id === id ? { ...i, ...updated } : i)));
  };

  const filtered = items.filter(b => {
    if (!search) return true;
    const q = search.toLowerCase();
    return (b.business_name || '').toLowerCase().includes(q)
      || (b.contact_name || '').toLowerCase().includes(q)
      || (b.area || '').toLowerCase().includes(q);
  });

  const selected = items.find(i => i.id === selectedId) ?? null;

  if (loading) return <div className="flex items-center justify-center h-64 text-gray-500">Loading…</div>;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-semibold text-gray-900">Business Intakes</h1>
          <p className="text-sm text-gray-500">{items.length} total</p>
        </div>
        <button onClick={load} className="p-2 bg-white border border-gray-300 rounded-lg hover:bg-gray-50">
          <RefreshCw className="w-4 h-4 text-gray-600" />
        </button>
      </div>

      {error && <ErrorAlert error={error} />}

      <input
        type="text"
        placeholder="Search name, contact, area…"
        value={search}
        onChange={e => setSearch(e.target.value)}
        className="w-full max-w-sm px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
      />

      <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 text-xs text-gray-500 uppercase tracking-wider">
            <tr>
              <th className="px-4 py-3 text-left font-medium">Business</th>
              <th className="px-4 py-3 text-left font-medium">Category</th>
              <th className="px-4 py-3 text-left font-medium">Area</th>
              <th className="px-4 py-3 text-left font-medium">Contact</th>
              <th className="px-4 py-3 text-left font-medium">Priority</th>
              <th className="px-4 py-3 text-left font-medium">Status</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {filtered.map(b => (
              <tr key={b.id} onClick={() => setSelectedId(b.id)} className="hover:bg-gray-50 cursor-pointer">
                <td className="px-4 py-3 font-medium text-gray-900">{b.business_name}</td>
                <td className="px-4 py-3 text-gray-600">{b.category || '—'}</td>
                <td className="px-4 py-3 text-gray-600">{b.area || '—'}</td>
                <td className="px-4 py-3 text-gray-600">{b.contact_name || '—'}</td>
                <td className="px-4 py-3">
                  {b.admin_priority && <StatusBadge status={b.admin_priority} variant="pill" />}
                </td>
                <td className="px-4 py-3">
                  {b.admin_review_status && <StatusBadge status={b.admin_review_status} />}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {filtered.length === 0 && <p className="text-center py-10 text-gray-400 text-sm">No businesses found</p>}
      </div>

      {selected && (
        <BusinessModal
          biz={selected}
          onClose={() => setSelectedId(null)}
          onSave={handleSave}
        />
      )}
    </div>
  );
}
